import { useState, useEffect, useCallback } from 'react'
import { FileText, Download, Printer, ZoomIn, ZoomOut, Maximize2, RefreshCw, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

export interface DocumentPreviewProps {
  templateId?: string
  templateName?: string
  previewContent?: string
  previewUrl?: string
  isLoading?: boolean
  error?: string | null
  onRefresh?: () => void
  onDownload?: () => void
  onPrint?: () => void
  className?: string
}

export function DocumentPreview({
  templateId,
  templateName,
  previewContent,
  previewUrl,
  isLoading = false,
  error = null,
  onRefresh,
  onDownload,
  onPrint,
  className,
}: DocumentPreviewProps) {
  const [zoom, setZoom] = useState(100)
  const [isFullscreen, setIsFullscreen] = useState(false)

  const handleZoomIn = useCallback(() => {
    setZoom((prev) => Math.min(prev + 25, 200))
  }, [])

  const handleZoomOut = useCallback(() => {
    setZoom((prev) => Math.max(prev - 25, 50))
  }, [])

  const handleFullscreen = useCallback(() => {
    setIsFullscreen(!isFullscreen)
  }, [isFullscreen])

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        if (e.key === '=') {
          e.preventDefault()
          handleZoomIn()
        } else if (e.key === '-') {
          e.preventDefault()
          handleZoomOut()
        } else if (e.key === '0') {
          e.preventDefault()
          setZoom(100)
        }
      }
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isFullscreen, handleZoomIn, handleZoomOut])

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex flex-col items-center justify-center h-full gap-4 p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Generating preview...</p>
        </div>
      )
    }

    if (error) {
      return (
        <div className="flex flex-col items-center justify-center h-full gap-4 p-8">
          <div className="p-3 rounded-full bg-red-100 dark:bg-red-900/30">
            <AlertCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
          </div>
          <div className="text-center">
            <p className="text-sm font-medium text-gray-900 dark:text-white">Preview Error</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{error}</p>
          </div>
          {onRefresh && (
            <Button variant="outline" size="sm" onClick={onRefresh} className="rounded-full">
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          )}
        </div>
      )
    }

    if (!previewContent && !previewUrl) {
      return (
        <div className="flex flex-col items-center justify-center h-full gap-4 p-8">
          <div className="p-4 rounded-2xl bg-gray-100 dark:bg-gray-800">
            <FileText className="h-12 w-12 text-gray-400" />
          </div>
          <div className="text-center">
            <p className="text-sm font-medium text-gray-900 dark:text-white">No Preview Available</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {templateId ? 'Fill in variables to see a preview' : 'Select a template to get started'}
            </p>
          </div>
        </div>
      )
    }

    if (previewUrl) {
      return (
        <iframe
          src={previewUrl}
          title={`Preview: ${templateName || 'Document'}`}
          className="w-full h-full border-0"
          style={{ transform: `scale(${zoom / 100})`, transformOrigin: 'top left' }}
        />
      )
    }

    return (
      <div
        className="p-8 bg-white dark:bg-gray-900 min-h-full"
        style={{ transform: `scale(${zoom / 100})`, transformOrigin: 'top left' }}
      >
        <div
          className="prose prose-sm dark:prose-invert max-w-none"
          dangerouslySetInnerHTML={{ __html: previewContent || '' }}
        />
      </div>
    )
  }

  return (
    <div
      className={cn(
        'flex flex-col rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 overflow-hidden',
        isFullscreen && 'fixed inset-0 z-50 rounded-none',
        className
      )}
    >
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {templateName || 'Document Preview'}
          </span>
        </div>

        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleZoomOut}
            disabled={zoom <= 50}
            className="h-8 w-8 p-0 rounded-lg"
            title="Zoom out (Ctrl+-)"
          >
            <ZoomOut className="h-4 w-4" />
          </Button>
          <span className="text-xs text-gray-500 w-12 text-center">{zoom}%</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleZoomIn}
            disabled={zoom >= 200}
            className="h-8 w-8 p-0 rounded-lg"
            title="Zoom in (Ctrl++)"
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
          <div className="w-px h-4 bg-gray-200 dark:bg-gray-700 mx-1" />
          {onRefresh && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onRefresh}
              disabled={isLoading}
              className="h-8 w-8 p-0 rounded-lg"
              title="Refresh preview"
            >
              <RefreshCw className={cn('h-4 w-4', isLoading && 'animate-spin')} />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleFullscreen}
            className="h-8 w-8 p-0 rounded-lg"
            title="Toggle fullscreen (Esc to exit)"
          >
            <Maximize2 className="h-4 w-4" />
          </Button>
          <div className="w-px h-4 bg-gray-200 dark:bg-gray-700 mx-1" />
          {onPrint && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onPrint}
              disabled={isLoading || !!error}
              className="h-8 w-8 p-0 rounded-lg"
              title="Print"
            >
              <Printer className="h-4 w-4" />
            </Button>
          )}
          {onDownload && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onDownload}
              disabled={isLoading || !!error}
              className="h-8 w-8 p-0 rounded-lg"
              title="Download"
            >
              <Download className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Preview Area */}
      <div className="flex-1 overflow-auto">{renderContent()}</div>
    </div>
  )
}

export function DocumentPreviewSkeleton() {
  return (
    <div className="flex flex-col rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <Skeleton className="h-4 w-32" />
        <div className="flex items-center gap-2">
          <Skeleton className="h-8 w-8 rounded-lg" />
          <Skeleton className="h-8 w-8 rounded-lg" />
          <Skeleton className="h-8 w-8 rounded-lg" />
        </div>
      </div>
      <div className="flex-1 p-8">
        <div className="space-y-4">
          <Skeleton className="h-6 w-3/4" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
          <div className="pt-4" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      </div>
    </div>
  )
}
